import { APP_CONFIGS } from "../constants";
import { IRequest, IRequestError } from "../interfaces/authentication.interface";
import {
    IModule,
    IModuleFavoriteResponse,
    IModuleSetProgressPayload,
    IUnitItemProgressResponse,
    IModuleResponse,
    IModulesResponse,
    IModuleUnit,
    IUnitResponse,
    IModuleProgress,
    IPathwayModulesResponse,
    IModulesGetProgressPayload,
    IModuleGetProgressPayload,
    IModuleUnitProgress
} from "../interfaces/modules.interface";
import { IUser } from "../interfaces/user.interface";
import { request, strapiRequest } from "./requests.service";



const getUserUnitItems = (unit: IModuleUnit, user?: IUser): IModuleUnit | undefined => {
    try {

        const activeSectorItems = unit.items.filter((item) => {
            // ITEM IS ACTIVE
            const isActive = item && "active" in item ? item.active : true;
            
            // ITEM HAS SECTOR
            const itemSectors = item && "sector" in item ? item.sector : false;
            
            // IF ITEM DOES NOT HAVE SECTOR OR THE USER DOES NOT HAVE ANY SECTOR ASSIGNED,
            // RETURN ITS ACTIVE STATUS
            if ((itemSectors && itemSectors.length === 0) || !itemSectors || !user?.company?.sector?.id) return isActive;
            
            // IF THE ITEM HAS SECTOR, CHECK IF THE SECTOR IS ONE OF THE USER'S
            const hasSector = itemSectors.includes(user?.company?.sector?.id);
            
            return isActive && hasSector;
        });

        if (activeSectorItems.length === 0) return;

        if (!user) return { ...unit, items: activeSectorItems };

        return { ...unit, items: activeSectorItems };
    } catch (e) { console.log(e) }
}

const items = `{
                    type: __typename
                    ... on ComponentItemVideo {
                        id
                        title
                        videoId
                        videoUrl
                        duration
                        active
                        videoDescription: description
                        thumbnail{
                            url
                        }
                        transcript {
                            url
                        }
                    }
                    ... on ComponentItemTypeForm {
                        id
                        formTitle: title
                        formDescription: description
                        videoUrl
                        active
                        formId
                        duration
                        sector
                    }
                    ... on ComponentItemRichText {
                        id
                        active
                        content
                        duration
                    }
                    ... on ComponentItemImage {
                        id
                        duration
                        active
                        image: file {
                            url
                            width
                            height
                            alt: alternativeText
                        }
                    }
                    ... on ComponentItemFile {
                        id
                        title
                        duration
                        active
                        files: file {
                            size
                            url
                            name
                            ext
                        }
                    }
                    ... on ComponentItemAudio {
                        id
                        title
                        description
                        duration
                        active
                        audio: file {
                            url
                        }
                        transcript {
                            url
                        }
                    }
                    ... on ComponentItemSliderImage {
                        id
                        title
                        active
                        duration
                        images {
                            url
                            alt: alternativeText
                        }
                        active
                    }
                    ... on ComponentItemVideoSlider {
                        id
                        title
                        content
                        active
                        duration
                        videos {
                            videoId
                            thumbnail {
                                url
                                alt: alternativeText
                            }
                        }
                        active
                    }
                    ... on ComponentItemInfoBanner {
                        id
                        title
                        description
                        active
                        link {
                            target
                            href
                            text
                        }
                        icon
                    }    
                    ... on ComponentItemGridContent {
                        id
                        title
                        active
                        duration
                        gridItems {
                            title
                            content
                        }
                    }
                    ... on Error {
                        code
                        message
                    }
                }
            `;

export const getModules = async (payload: {
    locale?: string;
    user?: IUser,
    filters?: any;
}): Promise<IRequest<IModule[]>> => {
    try {
        const { locale, filters } = payload;

        const query = `
        query Modules($locale: I18NLocaleCode, $filters: ModuleFiltersInput, $status: PublicationStatus) {
            modules(locale: $locale, filters: $filters, status: $status) {
                id: documentId
                title
                description
                contentAmount
                tag
                tag_color
                type
                image {
                    alt: alternativeText
                    width
                    height
                    url
                }
            }
        }`;

        const variables = {
            locale,
            status: "PUBLISHED",
            filters: {
                platforms: {
                    key: {
                        eq: APP_CONFIGS.key
                    }
                },
                sector: payload.user?.company?.sector?.id,
                ...filters
            }
        };

        const response = await strapiRequest.post<IModulesResponse>(`/graphql`, {
            operationName: "Modules",
            query,
            variables
        });

        return { success: true, data: response.data.data.modules.filter(item => item) ?? [] };
    } catch (error: any) {
        return { success: false, data: [] };
    }
};

export const getPathwayModules = async (payload: {
    pathway: string;
    locale?: string;
}): Promise<IRequest<string[]>> => {
    try {
        const { pathway } = payload;

        const query = `
        query Pathways($filters: PathwayFiltersInput) {
            pathways(filters: $filters) {
                value
                modules
            }
        }`;

        const variables = {
            filters: {
                value: {
                    eq: pathway
                }
            }
        };

        const response = await strapiRequest.post<IPathwayModulesResponse>(`/graphql`, {
            operationName: "Pathways",
            query,
            variables
        });
        const [pathwayResponse] = response.data.data.pathways;

        return {
            success: true,
            data: pathwayResponse.modules
        };
    } catch (error: any) {
        return { success: false, data: [] };
    }
};

export const getPsycoEducationalModule = async (payload: {
    locale?: string;
}): Promise<IRequest<IModule | undefined>> => {
    try {
        const { locale } = payload;

        const filters = {
            type: {
                eq: "PSYCHOEDUCATIONAL"
            }
        };

        const response = await getModules({ locale, filters });

        if (!response.success) throw Error();

        const [module] = response.data;

        return {
            success: true,
            data: module
        };
    } catch (error: any) {
        return { success: false, data: undefined };
    }
};

export const getFavoriteModules = async ({
    favoritesIds,
    locale
}: {
    favoritesIds: string[];
    locale: string;
}): Promise<IRequest<IModule[]>> => {
    try {
        const query = `
        query FavoriteModules($locale: I18NLocaleCode, $filters: ModuleFiltersInput, $status: PublicationStatus) {
            modules(locale: $locale, filters: $filters, status: $status) {
                id: documentId
                title
                description
                contentAmount
            }
        }`;

        const variables = {
            locale,
            status: "PUBLISHED",
            filters: {
                platforms: {
                    key: {
                        eq: APP_CONFIGS.key
                    }
                },
                documentId: {
                    in: favoritesIds.length ? favoritesIds : null
                }
            }
        };

        const response = await strapiRequest.post<IModulesResponse>(`/graphql`, {
            operationName: "FavoriteModules",
            query,
            variables
        });

        return { success: true, data: response.data.data.modules ?? [] };
    } catch (error: any) {
        return { success: false, data: [] as any };
    }
};

export const getModule = async (payload: {
    documentId?: string;
    user?: IUser,
    locale?: string;
    filters?: any;
}): Promise<IRequest<IModule>> => {
    try {
        const query = `
            query ModuleQuery($documentId: ID!, $locale: I18NLocaleCode, $status: PublicationStatus) {
                module(documentId: $documentId, locale: $locale, status: $status){
                    title
                    id: documentId
                    description
                    tag
                    tag_color
                    contentAmount
                    type
                    related_modules
                    related_dss {
                        title
                        formId
                        section
                    }
                    documents {
                        url
                        name
                        ext
                        size
                        alt: alternativeText
                    }
                    image {
                        alt: alternativeText
                        width
                        height
                        url
                    }
                    units {     
                        id: documentId               
                        title
                        tag
                        tag_color
                        items ${items}
                    }
                }
            }`;

        const variables = {
            ...payload,
            status: "PUBLISHED"
        };

        const response = await strapiRequest.post<IModuleResponse>(`/graphql`, {
            operationName: "ModuleQuery",
            query,
            variables
        });

        if (!response.data.data.module) throw Error;
        const module = response.data.data.module;
        const units = module.units?.map((unit) => getUserUnitItems(unit, payload.user)).filter(unit => unit) as IModuleUnit[];

        return {
            success: true,
            data: {
                ...module,
                units
            }
        };
    } catch (error: any) {
        return { success: false, data: null as any };
    }
};

export const getUnit = async (documentId: string, user?: IUser): Promise<IRequest<IModuleUnit>> => {
    try {
        const query = `
        query UnitQuery($documentId: ID!, $status: PublicationStatus) {
            unit(documentId: $documentId, status: $status) {
                id: documentId
                description
                title
                tag
                tag_color
                items ${items}
            }
    }`;

        const variables = {
            documentId,
            status: "PUBLISHED"
        };

        const response = await strapiRequest.post<IUnitResponse>(`/graphql`, {
            operationName: "UnitQuery",
            query,
            variables
        });

        if (!response.data.data.unit) throw Error;

        const unit = getUserUnitItems(response.data.data.unit, user);
        if (!unit) throw Error("No unit available");
        return { success: true, data: unit };
    } catch (error: any) {
        return { success: false, data: null as any };
    }
};

export const setModuleProgress = async (
    data: IModuleSetProgressPayload
): Promise<IRequest<IUnitItemProgressResponse> | IRequestError> => {
    try {
        const response = await request.post<IUnitItemProgressResponse>(
            `/${APP_CONFIGS.api.base}/content/module/progress`,
            data
        );

        return { success: true, data: response.data };
    } catch (error: any) {
        return { success: false, data: error };
    }
};

export const getModulesProgress = async (
    data: IModulesGetProgressPayload
): Promise<IRequest<IModuleProgress | []>> => {
    try {
        const response = await request.get<IModuleProgress>(
            `/${APP_CONFIGS.api.base}/content/module/progress?locale=${data.locale}`
        );

        return { success: true, data: response.data ?? [] };
    } catch (error: any) {
        return { success: false, data: [] };
    }
};

export const getModuleProgress = async (
    data: IModuleGetProgressPayload
): Promise<IRequest<IModuleUnitProgress | IRequestError>> => {
    try {
        const response = await request.get<IModuleUnitProgress>(
            `/${APP_CONFIGS.api.base}/content/module/progress/${data.moduleId}?locale=${data.locale}`
        );

        return { success: true, data: response.data };
    } catch (error: any) {
        return { success: false, data: error };
    }
};

export const setModuleFavorite = async (
    moduleId: string
): Promise<IRequest<any> | IRequestError> => {
    try {
        const response = await request.post<IUnitItemProgressResponse>(
            `/${APP_CONFIGS.api.base}/content/module/favourite/${moduleId}`
        );

        return { success: true, data: response.data };
    } catch (error: any) {
        return { success: false, data: error };
    }
};

export const setModuleUnfavorite = async (
    moduleId: string
): Promise<IRequest<any> | IRequestError> => {
    try {
        const response = await request.delete(
            `/${APP_CONFIGS.api.base}/content/module/favourite/${moduleId}`
        );

        return { success: true, data: response.data };
    } catch (error: any) {
        return { success: false, data: error };
    }
};

export const checkModuleFavorite = async (
    moduleId: string
): Promise<IRequest<IModuleFavoriteResponse | IRequestError>> => {
    try {
        const response = await request.get<IModuleFavoriteResponse>(
            `/${APP_CONFIGS.api.base}/content/module/favourite/${moduleId}`
        );

        return { success: true, data: response.data as IModuleFavoriteResponse };
    } catch (error: any) {
        return { success: false, data: error };
    }
};

export const getFavoriteModulesIds = async (): Promise<IRequest<string[] | IRequestError>> => {
    try {
        const response = await request.get<IModuleFavoriteResponse[]>(
            `/${APP_CONFIGS.api.base}/content/module/favourite`
        );

        return { success: true, data: response.data.map((d) => d.strapiModuleId) };
    } catch (error: any) {
        return { success: false, data: error };
    }
};